import { useTranslation } from "react-i18next";
import {
  IconChevronDown,
  IconChevronUp,
  IconHelpCircle,
} from "@tabler/icons-react";
import { JournalBandBadge } from "../ui/JournalBandBadge";
import {
  BAND_LABEL,
  BAND_QUARTILE,
  bandPhrase,
  type JournalBand,
} from "../../domains/journal/journalRank";
import type { Api } from "../../services/api";
import { appraisalGridText } from "./appraisalGridText";

/**
 * The works-score screen: what Juisci knows about the journal a paper came out
 * in, and how the study itself was designed and conducted. The two
 * halves below ARE that screen, wherever it is read — the article page's detail
 * sheet, and the sheet behind a cited source in Juisci GPT. They live here so
 * the two surfaces cannot drift into telling a reader two different things
 * about the same paper.
 *
 * Callers own the layout around them (a deep-linked scroll on the article page,
 * cited passages and retrieval signals in GPT) and hand over the journal band
 * with the name of its research field (`fieldBand`).
 */

// Both shapes are router-inferred from the article read every caller already
// makes — never redefined.
type ArticleRead =
  Api["get"]["/webapp/public/articles/:slug"]["output"]["body"];
/**
 * The appraisal the reader may see: a conduct verdict, the instrument that
 * produced it, the design it ran under, and the per-domain reading.
 *
 * The api projects this field to null for every state without a verdict, so
 * there is nothing for a surface to decide (`workAppraisal`, and
 * docs/works-score.md). A verdict is the whole claim this half makes: a work
 * without one states its journal standing alone, and no client can render a
 * half that names the design and apologises for the rest.
 */
export type ShownAppraisal = NonNullable<ArticleRead["appraisal"]>;

/**
 * All this screen asks of a translator: a key in, a string out.
 *
 * Narrower than either library's `TFunction` on purpose. Two translators reach
 * these helpers, a component's own `t` from `useTranslation` and the pinned one
 * from `useReadingTranslation`, and `i18next` and `react-i18next` declare
 * mutually unassignable `TFunction` types for them. The screen never needs what
 * they disagree about. Same shape `studyDesignLabel` takes
 * (`domains/app/utils/getArticleTypeText.ts`).
 */
export type Translate = (
  key: string,
  options?: Record<string, string>
) => string;

/**
 * The language a reading is stated in.
 *
 * SIGN-OFF (Menie): the appraisal follows the WORK, not the reader's app. A
 * French article carries a French appraisal for whoever opens it, the same way
 * its title and its TLDR do. The reader's own locale decides the app around the
 * sheet; it does not decide what language this paper is discussed in.
 *
 * Surfaces with no work in hand pass the reader's language, which on those
 * surfaces is the language the conversation is being held in.
 */
export type ReadingLanguage = ArticleRead["language"];

/**
 * A `t` pinned to one language, for a card that must read as one piece.
 *
 * Everything the ledger says is pinned by it: the grid text, the judgment
 * words, the answer chips, the design label, the headings. Pinning only the
 * grid would move the seam rather than close it, because the verdict beside a
 * question would still follow the app.
 *
 * All supported translation bundles are available at boot, including the
 * work's language when it differs from the interface language.
 */
export function useReadingTranslation(language: ReadingLanguage): Translate {
  return useTranslation().i18n.getFixedT(language);
}

/** The journal half's inputs, resolved by the caller. `journalName` is null
 * when the surface already names the journal above the screen. */
export type JournalStanding = {
  journalName: string | null;
  impactFactor: number | null;
  // Null when the journal has no banded research field.
  band: { band: JournalBand; field: string } | null;
  indexing: readonly string[];
  isCddRecommended: boolean;
};

// ── The appraisal ─────────────────────────────────────────────────────────────

// The resolved design the appraisal ran under — the detailed sub-label for
// appraised works ("Cohort study", not the coarse tier bucket).
export function designLabel(
  t: Translate,
  design: ShownAppraisal["design"]
): string {
  switch (design) {
    case "randomized_trial":
      return t("Randomized trial");
    case "non_randomized_interventional":
      return t("Non-randomized trial");
    case "systematic_review":
      return t("Systematic review or meta-analysis");
    case "cohort":
      return t("Cohort study");
    case "case_control":
      return t("Case-control study");
    case "cross_sectional":
      return t("Cross-sectional study");
    case "diagnostic_accuracy":
      return t("Diagnostic accuracy study");
    case "prognostic":
      return t("Prognostic study");
    case "qualitative":
      return t("Qualitative study");
    default:
      throw new Error(design satisfies never);
  }
}

/**
 * The risk scale, as every surface states it: the magnitude word, its DS colour,
 * and how much of the three-segment meter it fills.
 *
 * The meter measures the quantity the section NAMES (SIGN-OFF): the heading
 * reads "Risk of bias", so more segments mean more risk, and high risk fills
 * all three. The green-to-red ramp carries whether that quantity is good news,
 * which is the job a bare length cannot do. `unclear` is not a fourth
 * magnitude — the appraisal carried no usable signal, so it fills nothing and
 * greys the word: an empty meter reads as "not established", which is exactly
 * what it is.
 *
 * The word is the BARE magnitude ("High", not "High risk"): every surface that
 * states it has already named the section "Risk of bias" right beside it, and a
 * value repeating its own label says the same word twice. The per-domain
 * judgments keep their fuller wording (`judgmentDisplay`) — those rows are
 * labelled by the domain, not by the section.
 *
 * Label, colour and fill are one function on purpose: three call sites read
 * them (both headlines and the meter), and a verdict whose word and meter
 * disagreed would be a lie no type could catch.
 */
function riskLevelDisplay(
  t: Translate,
  riskLevel: ShownAppraisal["riskLevel"]
): {
  label: string;
  text: string;
  fill: string;
  filled: 0 | 1 | 2 | 3;
} {
  switch (riskLevel) {
    case "low":
      return {
        label: t("Low"),
        text: "text-secondary-green-1",
        fill: "bg-secondary-green-1",
        filled: 1,
      };
    case "moderate":
      return {
        label: t("Moderate"),
        text: "text-secondary-orange-2",
        fill: "bg-secondary-orange-2",
        filled: 2,
      };
    case "high":
      return {
        label: t("High"),
        text: "text-ds-strawberry-600",
        fill: "bg-ds-strawberry-600",
        filled: 3,
      };
    case "unclear":
      return {
        label: t("Unclear"),
        text: "text-ds-slate-600",
        fill: "bg-ds-slate-400",
        filled: 0,
      };
    default:
      throw new Error(riskLevel satisfies never);
  }
}

function JudgmentDot({
  judgment,
}: {
  judgment: ShownAppraisal["domains"][number]["judgment"];
}) {
  const meta = (() => {
    switch (judgment) {
      case "low":
        return { symbol: "+", bg: "bg-secondary-green-1" };
      case "some_concerns":
        return { symbol: "?", bg: "bg-secondary-orange-2" };
      case "high":
        return { symbol: "\u2212", bg: "bg-ds-strawberry-600" };
      case "unclear":
        return { symbol: "?", bg: "bg-ds-slate-400" };
      default:
        throw new Error(judgment satisfies never);
    }
  })();
  return (
    <span
      className={`inline-flex size-5 items-center justify-center rounded-full text-xs font-bold text-white ${meta.bg}`}
    >
      {meta.symbol}
    </span>
  );
}

function judgmentDisplay(
  t: Translate,
  judgment: ShownAppraisal["domains"][number]["judgment"]
): { label: string; text: string } {
  switch (judgment) {
    case "low":
      return { label: t("Low risk"), text: "text-secondary-green-1" };
    case "some_concerns":
      return { label: t("Some concerns"), text: "text-secondary-orange-2" };
    case "high":
      return { label: t("High risk"), text: "text-ds-strawberry-600" };
    case "unclear":
      return { label: t("Unclear"), text: "text-ds-slate-600" };
    default:
      throw new Error(judgment satisfies never);
  }
}

// The model's factual answer to one signalling question. No hue at all — the
// families are told apart by FILL: the yes-family keeps the white chip, the
// no-family gets a filled grey chip, and the no-signal answers drop the chip
// and fade to bare text (the probably_* pair reads one shade lighter than its
// plain counterpart). Deliberately never the risk palette: a "yes" can mean
// higher or lower risk depending on the question's polarity (the registry
// applies it when deriving the judgment), so a colored answer would collide
// with the judgment colors and mislead.
type SignalAnswer =
  ShownAppraisal["domains"][number]["signals"][number]["answer"];

function answerDisplay(
  t: Translate,
  answer: SignalAnswer
): { label: string; chip: string } {
  switch (answer) {
    case "yes":
      return { label: t("Yes"), chip: "bg-white text-ds-slate-800" };
    case "probably_yes":
      return { label: t("Probably yes"), chip: "bg-white text-ds-slate-500" };
    case "probably_no":
      return {
        label: t("Probably no"),
        chip: "bg-ds-slate-200 text-ds-slate-600",
      };
    case "no":
      return { label: t("No"), chip: "bg-ds-slate-200 text-ds-slate-800" };
    case "no_information":
      return {
        label: t("No information"),
        chip: "bg-transparent text-ds-slate-400",
      };
    case "not_applicable":
      return { label: t("n/a"), chip: "bg-transparent text-ds-slate-400" };
    default:
      throw new Error(answer satisfies never);
  }
}

// The instrument name, bolded inside the already-translated attribution
// phrase — located in the string rather than concatenated keys, same idiom as
// BandPhraseText (word order stays the translator's).
function InstrumentPhraseText({
  phrase,
  instrument,
}: {
  phrase: string;
  instrument: string;
}) {
  const at = phrase.indexOf(instrument);
  if (at === -1) return phrase;
  return (
    <>
      {phrase.slice(0, at)}
      <span className="font-semibold text-ds-slate-500">{instrument}</span>
      {phrase.slice(at + instrument.length)}
    </>
  );
}

function Overline({ children }: { children: string }) {
  return (
    <span className="text-xs font-semibold uppercase tracking-wider text-ds-slate-500">
      {children}
    </span>
  );
}

// The percentile phrase for the quartile pill: everything up to the field
// name reads bold ("Top 10% in"), the field itself fades grey — split by
// locating the name in the already-translated string, so word order stays the
// translator's. A phrase without the name (a_plus's "Flagship journal") reads
// whole-bold.
function BandPhraseText({ phrase, field }: { phrase: string; field: string }) {
  const at = phrase.indexOf(field);
  if (at === -1)
    return <span className="font-semibold text-ds-black">{phrase}</span>;
  return (
    <>
      <span className="font-semibold text-ds-black">{phrase.slice(0, at)}</span>
      <span className="text-ds-slate-500">
        {field}
        {phrase.slice(at + field.length)}
      </span>
    </>
  );
}

/** A ledger row that explains itself: the "?" opens one sentence under it. */
function ExplainedLedgerRow({
  label,
  explanation,
  children,
}: {
  label: string;
  explanation: string;
  children: React.ReactNode;
}) {
  return (
    <details className="group border-b border-ds-slate-100">
      <summary className="flex cursor-pointer list-none items-baseline gap-3 py-2.5 [&::-webkit-details-marker]:hidden">
        <span className="flex shrink-0 items-center gap-1 text-footnote font-semibold text-ds-slate-500">
          {label}
          <IconHelpCircle
            size={13}
            className="text-ds-slate-400 group-open:hidden"
          />
          <IconChevronUp
            size={13}
            className="hidden text-ds-slate-400 group-open:inline"
          />
        </span>
        <span className="flex-1 text-right text-footnote font-medium text-ds-slate-950">
          {children}
        </span>
      </summary>
      <p className="m-0 pb-2.5 text-caption/snug text-ds-slate-500">
        {explanation}
      </p>
    </details>
  );
}

/** One label/value line of the journal ledger. */
function LedgerRow({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    // The label holds its width and the value wraps under it: a long
    // field name ("Physical and Rehabilitation Medicine") used to shrink the
    // label away entirely, leaving a percentile with nothing naming it.
    <div className="flex items-baseline gap-3 border-b border-ds-slate-100 py-2.5 last:border-b-0">
      <span className="shrink-0 text-footnote font-semibold text-ds-slate-500">
        {label}
      </span>
      <span className="flex-1 text-right text-footnote font-medium text-ds-slate-950">
        {children}
      </span>
    </div>
  );
}

/** The heading over each half, so every surface titles them the same way. */
/** A section heading that carries its own definition behind a `?`, the same
 * affordance the journal half's rows use. The explanation opens in place, so a
 * reader who already knows the term never pays for it. */
function ExplainedSectionHeading({
  children,
  explanation,
}: {
  children: string;
  explanation: readonly string[];
}) {
  return (
    <details className="group">
      <summary className="flex cursor-pointer list-none items-center gap-1.5 [&::-webkit-details-marker]:hidden">
        <SectionHeading>{children}</SectionHeading>
        <IconHelpCircle
          size={15}
          className="text-ds-slate-400 group-open:hidden"
        />
        <IconChevronUp
          size={15}
          className="hidden text-ds-slate-400 group-open:inline"
        />
      </summary>
      <p className="m-0 pt-2 text-footnote text-ds-slate-500">
        {explanation.join(" ")}
      </p>
    </details>
  );
}

function SectionHeading({ children }: { children: string }) {
  return (
    <span className="text-ui-body font-semibold uppercase text-ds-black">
      {children}
    </span>
  );
}

/**
 * Whether a standing states anything at all.
 *
 * A journal Juisci holds but has neither ranked, scored nor indexed says
 * nothing worth a section. Every caller asks it before rendering the section,
 * because the caller also draws what surrounds it (a divider, a door into the
 * sheet), and an empty block must take those with it.
 */
export function journalStatesAnything(standing: JournalStanding): boolean {
  return (
    standing.impactFactor !== null ||
    standing.band !== null ||
    standing.indexing.length > 0 ||
    standing.isCddRecommended
  );
}

/** The journal half: the venue's headline metrics, then the ledger behind them. */
export function JournalStandingSection({
  journalName,
  impactFactor,
  band,
  indexing,
  isCddRecommended,
}: JournalStanding) {
  const { t } = useTranslation();
  const impact = impactFactor === null ? null : impactFactor.toFixed(1);
  // Rendering the headings over empty rows would read as "measured, and it is
  // nothing". The caller filters, see `journalStatesAnything`.
  if (
    !journalStatesAnything({
      journalName,
      impactFactor,
      band,
      indexing,
      isCddRecommended,
    })
  )
    throw new Error(
      "should not happen: a journal standing that states nothing reached the section"
    );

  return (
    <div className="flex flex-col gap-3">
      <SectionHeading>{t("Journal")}</SectionHeading>
      {journalName !== null && (
        <span className="text-ui-body font-bold text-ds-slate-950">
          {journalName}
        </span>
      )}
      {(impact !== null || band !== null) && (
        // `items-stretch` so both overlines share the top line: the rank's
        // badge is taller than the impact factor's number, and bottom-aligning
        // the two columns would drop its label out of line.
        <div className="flex items-stretch justify-between gap-3">
          {impact !== null && (
            <span className="flex flex-col gap-1.5">
              <Overline>{t("Impact factor")}</Overline>
              <span className="flex flex-1 items-center text-heading-lg font-semibold tabular-nums text-ds-slate-950">
                {impact}
              </span>
            </span>
          )}
          {/* The mark carries no caption: the Rank row below reads it out in
              full ("A - Top 10% in Cardiology"), so a label above it would
              only say the same thing twice. */}
          {band !== null && (
            <span className="flex flex-col items-end justify-end">
              <JournalBandBadge band={band.band} variant="lg" />
            </span>
          )}
        </div>
      )}
      <div className="flex flex-col">
        {band !== null && (
          <ExplainedLedgerRow
            label={t("Rank")}
            explanation={t(
              "Rank reflects the journal's impact factor percentile within its research field."
            )}
          >
            {/* The letter repeats the mark above on purpose: the ledger is the
                reading of the mark, and a row that jumped straight to the
                percentile would leave the letter unexplained. The quartile
                leads it, the wording a clinician already ranks by. */}
            <span className="mr-2 inline-block rounded-full bg-ds-mint-100 px-2 py-0.5 align-middle text-caption font-bold text-ds-mint-800">
              {BAND_QUARTILE[band.band]}
            </span>
            {BAND_LABEL[band.band]}
            {" · "}
            <BandPhraseText
              phrase={bandPhrase(t, band.band, band.field)}
              field={band.field}
            />
          </ExplainedLedgerRow>
        )}
        {impact !== null && (
          <ExplainedLedgerRow
            label={t("Impact factor")}
            explanation={t(
              "The impact factor is the average number of citations that the journal's recent articles received."
            )}
          >
            {impact}
          </ExplainedLedgerRow>
        )}
        {indexing.length > 0 && (
          <LedgerRow label={t("Indexed in")}>{indexing.join(" · ")}</LedgerRow>
        )}
        {/* The CDD-CNU Santé "revues recommandables" list is a
            recommendation, not an index, so it gets its own wording. */}
        {isCddRecommended && (
          <LedgerRow label={t("Recommended by")}>{"CDD-CNU Santé"}</LedgerRow>
        )}
      </div>
    </div>
  );
}

// ── The level-of-evidence half ────────────────────────────────────────────────

/**
 * The appraisal half: the conduct verdict, what was appraised, and the
 * instrument's own per-domain reading under it.
 *
 * No composite score. The half states the two facts Juisci holds about a
 * paper's method (its design, and its risk of bias) side by side, and never
 * folds them into a single grade: a per-paper reading has one of the five
 * downgrade domains a certainty rating needs, so a rating is not ours to make.
 */
type ShownTarget = ShownAppraisal["assessmentTarget"];
type ShownResult = Extract<ShownTarget, { state: "selected" }>["result"];
type ShownScope = Extract<ShownTarget, { state: "captured" }>["scope"];

/** One line of the box: a translated label, and the source's own phrase. */
type AssessedRow = { label: string; value: string | null };

/**
 * The stored phrase is lifted mid-sentence out of the paper ("dry eye disease
 * (DED)"), so it starts lowercase. ONLY the first character is raised: the
 * rest carries acronyms and assay names (aHR, CRSD, KCCQ, HbA1c) that any
 * broader casing pass would flatten.
 */
function sentenceCase(value: string): string {
  return `${value.charAt(0).toUpperCase()}${value.slice(1)}`;
}

/**
 * The three closing rows every result shape and the association scope share.
 * Structural, not nominal: each caller passes its own object and the shared
 * context fields are the only ones read.
 */
function contextRows(
  t: Translate,
  context: {
    timepoint: string | null;
    analysisPopulation: string | null;
    analysis: string | null;
  }
): AssessedRow[] {
  return [
    { label: t("Timepoint"), value: context.timepoint },
    { label: t("Analysis population"), value: context.analysisPopulation },
    { label: t("Analysis"), value: context.analysis },
  ];
}

function resultRows(t: Translate, result: ShownResult): AssessedRow[] {
  switch (result.kind) {
    case "intervention":
      return [
        { label: t("Population"), value: result.population },
        { label: t("Intervention"), value: result.intervention },
        { label: t("Comparator"), value: result.comparator },
        { label: t("Outcome"), value: result.outcome },
        ...contextRows(t, result),
      ];
    case "prognostic":
      return [
        { label: t("Population"), value: result.population },
        { label: t("Prognostic factor"), value: result.factor },
        { label: t("Outcome"), value: result.outcome },
        ...contextRows(t, result),
      ];
    case "diagnostic":
      return [
        { label: t("Population"), value: result.population },
        { label: t("Index test"), value: result.test },
        { label: t("Target condition"), value: result.condition },
        { label: t("Reference standard"), value: result.referenceStandard },
        { label: t("Positivity threshold"), value: result.threshold },
        ...contextRows(t, result),
      ];
    default:
      throw new Error(result satisfies never);
  }
}

function scopeRows(t: Translate, scope: ShownScope): AssessedRow[] {
  switch (scope.kind) {
    case "association":
      return [
        { label: t("Population"), value: scope.population },
        { label: t("Exposure"), value: scope.exposure },
        { label: t("Comparator"), value: scope.comparator },
        { label: t("Outcome"), value: scope.outcome },
        ...contextRows(t, scope),
      ];
    case "review":
      return [
        { label: t("Review question"), value: scope.question },
        { label: t("Population"), value: scope.population },
        {
          label: t("Intervention or exposure"),
          value: scope.interventionOrExposure,
        },
        { label: t("Comparator"), value: scope.comparator },
        {
          label: t("Outcomes"),
          // Every item is raised, not just the first: a joined list reads as
          // several phrases, so casing only the head leaves the rest looking
          // like a sentence that broke.
          value:
            scope.outcomes.length === 0
              ? null
              : scope.outcomes
                  .map((outcome) =>
                    outcome.timepoint === null
                      ? sentenceCase(outcome.name)
                      : `${sentenceCase(outcome.name)} (${outcome.timepoint})`
                  )
                  .join(" · "),
        },
        {
          label: t("Included study designs"),
          value:
            scope.includedStudyDesigns.length === 0
              ? null
              : scope.includedStudyDesigns.map(sentenceCase).join(" · "),
        },
        { label: t("Synthesis"), value: scope.synthesis },
      ];
    case "qualitative":
      return [
        { label: t("Research aim"), value: scope.aim },
        { label: t("Phenomenon"), value: scope.phenomenon },
        { label: t("Participants"), value: scope.participants },
        { label: t("Setting"), value: scope.setting },
        { label: t("Data collection"), value: scope.dataCollection },
        { label: t("Timepoint"), value: scope.timepoint },
        { label: t("Analysis"), value: scope.analysis },
      ];
    default:
      throw new Error(scope satisfies never);
  }
}

/**
 * Two target states carry nothing a reader can use. `not_recorded` is an
 * appraisal made before the pipeline captured a target at all, and
 * `capture_unresolved` is one whose capture failed source verification. Both
 * render NOTHING here, deliberately: a line saying the result was not
 * recorded tells the reader about our pipeline rather than about the paper,
 * and the admin panel is where a failed capture is diagnosed.
 */
function assessedRows(t: Translate, target: ShownTarget): AssessedRow[] {
  switch (target.state) {
    case "not_recorded":
    case "capture_unresolved":
      return [];
    case "selected":
      return resultRows(t, target.result);
    case "captured":
      return scopeRows(t, target.scope);
    default:
      throw new Error(target satisfies never);
  }
}

/** One row of the card: every fact gets the same label and the same value
 *  treatment, so no row reads as ranked above another. */
/** The verdict row when there is no reading behind it: same words, no chevron
 *  and no disclosure. `domains` is typed as an array, so an instrument that
 *  reported none would otherwise offer a row that opens onto nothing. */
function VerdictRow({
  label,
  word,
  dot,
  text,
}: {
  label: string;
  word: string;
  dot: string;
  text: string;
}) {
  return (
    <div className="py-2.5">
      <span className="block text-caption text-ds-slate-500">{label}</span>
      <span className="flex items-center gap-1.5">
        <span
          aria-hidden="true"
          className={`size-1.5 shrink-0 rounded-full ${dot}`}
        />
        <span className={`text-sm/snug font-semibold ${text}`}>{word}</span>
      </span>
    </div>
  );
}

function ScopeRow({ label, value }: { label: string; value: string }) {
  return (
    <div className="border-b border-dashed border-secondary-bluegrey-2 py-2.5">
      <span className="block text-caption text-ds-slate-500">{label}</span>
      <span className="block text-sm/snug text-ds-black">{value}</span>
    </div>
  );
}

/**
 * What the appraisal was about, labelled and in reading order: the design,
 * then the population before the thing done to it, then the comparison, then
 * the outcome. PICO order for a trial and the matching order for the other
 * five shapes — never the stored object's key order, which is a storage fact
 * and reads as noise.
 *
 * SIGN-OFF (Menie): every row is a PEER. The design is not a heading over the
 * PICO rows and the risk of bias is not a headline over either: it is the
 * last and quietest row, a door into its own screen where the meter, the
 * instrument and every domain are read. A verdict shouted on this card
 * outranks the facts that qualify it.
 *
 * `flatMap` rather than `filter`: it narrows `value` to `string`, so a row
 * renders without a non-null assertion.
 */
function StudyScopeCard({
  level,
  language,
}: {
  level: ShownAppraisal;
  language: ReadingLanguage;
}) {
  const t = useReadingTranslation(language);
  const risk = riskLevelDisplay(t, level.riskLevel);
  const rows = assessedRows(t, level.assessmentTarget).flatMap((row) =>
    row.value === null ? [] : [{ label: row.label, value: row.value }]
  );
  return (
    <div className="flex flex-col rounded-2xl bg-secondary-soft-light-yellow-grey px-3.5 py-1">
      <ScopeRow
        label={t("Study design")}
        value={designLabel(t, level.design)}
      />
      {rows.map((row) => (
        <ScopeRow
          key={row.label}
          label={row.label}
          value={sentenceCase(row.value)}
        />
      ))}
      {/* A DOT, and nothing louder: this row is meant to be read last, after
          the design and the result it was read against. The reading opens UNDER the
          row rather than replacing the card — the row stays on screen as the
          heading of what it revealed, so the reader never loses the study the
          reading is about.
          Native `<details>` with a NAMED `group-open/appraisal:` variant, the
          the domain rows below already use in this file: no `useState` in a
          business component (`apps/webapp/AGENTS.md` §3), and the open state
          lives where the element does. */}
      {level.domains.length === 0 ? (
        <VerdictRow
          label={t("Risk of bias")}
          word={risk.label}
          dot={risk.fill}
          text={risk.text}
        />
      ) : (
        <details className="group/appraisal">
          <summary className="flex cursor-pointer list-none items-center justify-between gap-3 py-2.5 [&::-webkit-details-marker]:hidden">
            <span className="flex min-w-0 flex-col">
              <span className="block text-caption text-ds-slate-500">
                {t("Risk of bias")}
              </span>
              <span className="flex items-center gap-1.5">
                <span
                  aria-hidden="true"
                  className={`size-1.5 shrink-0 rounded-full ${risk.fill}`}
                />
                {/* The verdict word alone. SIGN-OFF (Menie): no instrument here.
                    The reading this row opens attributes itself in full
                    ("Reading by Juisci based on X"), so naming the tool on the
                    closed row states it twice and spends the row's quiet on a
                    fact the reader has no use for until they open it. */}
                <span className={`text-sm/snug font-semibold ${risk.text}`}>
                  {risk.label}
                </span>
              </span>
            </span>
            {/* NAMED group. `group-open:` matches ANY ancestor `.group[open]`,
                and the domain rows inside the reading are themselves `group`
                disclosures — an unnamed variant here flipped their chevrons
                too, the moment this row opened. */}
            <IconChevronDown className="size-4 shrink-0 text-ds-slate-400 transition-transform group-open/appraisal:rotate-180" />
          </summary>
          <div className="border-t border-dashed border-secondary-bluegrey-2">
            <AppraisalReading level={level} language={language} />
          </div>
        </details>
      )}
    </div>
  );
}

/**
 * The appraisal as a whole section: the heading, the verdict, the design and
 * the reading. Used where the appraisal stands ALONE (the GPT source sheet),
 * with nothing above it that already states those facts.
 */
/**
 * The READING itself: the verdict, its meter, the instrument attribution and
 * every domain. Split out of `StudyAppraisalLedger` so the works-score sheet
 * can open it UNDER the card's risk-of-bias row, where the heading and the
 * design are already stated one row above and must not be repeated.
 */
function AppraisalReading({
  level,
  language,
}: {
  level: ShownAppraisal;
  language: ReadingLanguage;
}) {
  const t = useReadingTranslation(language);
  const risk = riskLevelDisplay(t, level.riskLevel);
  return (
    <div className="flex flex-col">
      {level.domains.length > 0 && (
        // No card chrome of its own: the reading is always rendered INSIDE
        // `StudyScopeCard`, which already draws the rounded tinted box and its
        // horizontal padding. A second identical box here nested one inside
        // the other and indented the instrument line and every domain past
        // the PICO rows above them.
        <div className="flex flex-col pb-1 pt-3.5">
          {/* The box states its own provenance first: which instrument was
              answered, then what it concluded, then the shape of that
              conclusion, then the domains. The attribution sits HERE and not
              under the headline, because it describes this reading and would
              otherwise name the instrument twice on one screen. */}
          <span className="pb-2.5 text-footnote text-ds-slate-500">
            <InstrumentPhraseText
              phrase={t("Reading by Juisci based on {{instrument}}", {
                instrument: level.instrumentLabel,
              })}
              instrument={level.instrumentLabel}
            />
          </span>
          {level.domains.map((domain, index) => {
            const judgment = judgmentDisplay(t, domain.judgment);
            const label = (
              <span className="flex items-baseline gap-2.5">
                <span className="text-caption text-ds-slate-400">
                  D{index + 1}
                </span>
                <span className="text-sm/snug text-ds-black">
                  {appraisalGridText(t, domain.id, domain.label)}
                </span>
              </span>
            );
            // A one-item checklist domain (JBI/CASP) IS its own question,
            // so it answers in the checklist's native vocabulary: the
            // yes/no word, colored by the derived judgment. Safe polarity:
            // checklist items are always phrased so yes = lower risk.
            // Nothing to expand — the judgment and the answer are the
            // same fact.
            // `length === 1` is what proves the read: the guard runs
            // first, so the row is only ever indexed when it is there.
            // Compared on the registry English the api sent, not on the
            // rendered wording: both sides share one i18n key, so the
            // comparison would hold either way, and the raw one holds even
            // for a verdict whose id this build cannot translate.
            const firstSignal = domain.signals[0];
            const ownAnswer =
              domain.signals.length === 1 &&
              firstSignal.question === domain.label
                ? firstSignal.answer
                : null;
            if (ownAnswer !== null || domain.signals.length === 0) {
              return (
                <div
                  key={domain.id}
                  className="flex items-baseline justify-between gap-3 border-b border-dashed border-secondary-bluegrey-2 py-3 last:border-b-0"
                >
                  {label}
                  <span
                    className={`shrink-0 whitespace-nowrap text-sm font-medium ${judgment.text}`}
                  >
                    {ownAnswer !== null
                      ? answerDisplay(t, ownAnswer).label
                      : judgment.label}
                  </span>
                </div>
              );
            }
            return (
              <details
                key={domain.id}
                className="group border-b border-dashed border-secondary-bluegrey-2 last:border-b-0"
              >
                <summary className="flex cursor-pointer list-none items-baseline justify-between gap-3 py-3 [&::-webkit-details-marker]:hidden">
                  {label}
                  <span
                    className={`flex shrink-0 items-center gap-1 self-center whitespace-nowrap text-sm font-medium ${judgment.text}`}
                  >
                    {judgment.label}
                    <IconChevronDown
                      size={14}
                      className="text-ds-slate-400 transition-transform group-open:rotate-180"
                    />
                  </span>
                </summary>
                <div className="flex flex-col gap-2.5 pb-3.5">
                  {domain.signals.map((signal) => {
                    const answer = answerDisplay(t, signal.answer);
                    return (
                      <div
                        key={signal.id}
                        className="flex items-baseline justify-between gap-3"
                      >
                        <span className="text-footnote text-ds-slate-600">
                          {appraisalGridText(t, signal.id, signal.question)}
                        </span>
                        <span
                          className={`shrink-0 rounded-lg px-1.5 py-0.5 text-caption font-medium ${answer.chip}`}
                        >
                          {answer.label}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </details>
            );
          })}
          {/* The verdict LAST, where it reads as the sum of the rows above it.
              The headline states it too, but the headline scrolls away, and a
              reader who has just read eight domains wants the total at the
              point they finish reading, not before they start. */}
          <details className="group border-t border-secondary-bluegrey-2">
            <summary className="flex cursor-pointer list-none flex-col gap-2 py-3 [&::-webkit-details-marker]:hidden">
              {/* The fingerprint leads, on its own line. Three things do not
                  fit one row: CASP asks 10 questions and JBI 8, and 8 dots
                  need 208px of the ~120px a row leaves beside the label and
                  the verdict. Shrinking the dot to buy that room takes the
                  +/-/? glyph below legible, and that glyph is the non-colour
                  cue, so the GAP gives way instead.
                  Sized for the narrowest reachable screen, not the common one.
                  The box is the viewport less 68px (the sheet's px-5 and this
                  box's px-3.5), so a 320px device leaves 252px. CASP's ten
                  dots are 10x20px plus nine gaps: 254px at gap-1.5, which
                  wraps by two, and 236px at gap-1, which fits with room over.
                  It takes its own line at three domains as well as at ten: one
                  layout reads the same for every instrument, and no locale's
                  longer word for "Overall" can push it into a ragged wrap.
                  Right-aligned, so it sits over the verdict it adds up to. */}
              {/* `aria-hidden` because the strip is a VISUAL summary of rows
                  that already state every judgment in words, right underneath.
                  Read aloud it is "plus, question mark, minus" with nothing to
                  attach each glyph to, and ten of those on the CASP checklist.
                  Hiding it drops a duplicate, not a fact. The glyph itself
                  stays: it is what carries the judgment for a reader who
                  cannot separate the red dot from the green one. */}
              <span
                aria-hidden="true"
                className="flex flex-wrap items-center justify-end gap-1"
              >
                {level.domains.map((domain) => (
                  <JudgmentDot key={domain.id} judgment={domain.judgment} />
                ))}
              </span>
              <span className="flex items-center justify-between gap-3">
                <span className="flex shrink-0 items-center gap-1 text-sm font-semibold text-ds-black">
                  {t("Overall")}
                  <IconHelpCircle
                    size={13}
                    className="text-ds-slate-400 group-open:hidden"
                  />
                  <IconChevronUp
                    size={13}
                    className="hidden text-ds-slate-400 group-open:inline"
                  />
                </span>
                <span className={`shrink-0 text-sm font-bold ${risk.text}`}>
                  {risk.label}
                </span>
              </span>
            </summary>
            {/* Keyed on the AGGREGATION, not the grid: the eight
                grids share two rules, so eight explanations would be six
                copies of two facts. */}
            <p className="m-0 pb-3 text-caption/snug text-ds-slate-500">
              {level.aggregation === "worst"
                ? t(
                    "This appraisal grid takes the worst single domain: one domain at high risk makes the whole reading high risk, whatever the other domains say."
                  )
                : `${t("This appraisal grid averages its domains. Low risk scores 1, some concerns or no information scores 0.5, and high risk scores 0.")} ${t("An average of 0.75 or more reads low, 0.5 or more reads moderate, and anything lower reads high.")}`}
            </p>
          </details>
        </div>
      )}
    </div>
  );
}

/**
 * The appraisal as a whole section: the heading over the card. Used wherever
 * the appraisal stands alone — the works-score sheet and the GPT source sheet.
 *
 * It composes `StudyScopeCard` rather than repeating the verdict and the
 * design: both surfaces must state what was assessed before they grade it, and
 * a second composition here is how the GPT sheet silently lost the assessed
 * result in the first place.
 */
export function StudyAppraisalLedger({
  level,
  language,
}: {
  level: ShownAppraisal;
  language: ReadingLanguage;
}) {
  const t = useReadingTranslation(language);
  return (
    <div className="flex flex-col pb-2">
      {/* SIGN-OFF (Menie): the heading names this half's SUBJECT, the way the
          journal half heads itself "Journal". It used to read "Risk of bias",
          written when the verdict was all this half stated; the card under it
          now opens on the design and the assessed result, so that heading
          labelled the wrong thing. The risk-of-bias definition it carried
          survives as the second sentence of the explanation, which has to
          cover every row of the card and not just the last one. */}
      <ExplainedSectionHeading
        explanation={[
          t(
            "Juisci records the design of a study and the result it appraised, in the paper's own words."
          ),
          t(
            "Risk of bias, at the end, is a reading of how the study was designed and run, and of whether its method could have moved that result away from the truth."
          ),
        ]}
      >
        {t("Study")}
      </ExplainedSectionHeading>
      {/* `mt-3` matches the journal half's `gap-3` between its own heading and
          the journal name, so the two halves of one sheet breathe alike. */}
      <div className="mt-3">
        <StudyScopeCard level={level} language={language} />
      </div>
    </div>
  );
}
